import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/layout/SiteNav";
import SiteFooter from "@/components/layout/SiteFooter";
import { ArrowRight, Eye, ShieldCheck } from "lucide-react";
import axios from "axios";

/**
 * Public, read-only "they found X dollars" landing page. Fed by /api/share/card/:code
 * — no auth, no PII. Returns 410 if the share was revoked.
 */
export default function Won() {
    const { code } = useParams();
    const [data, setData] = useState(null);
    const [gone, setGone] = useState(false);

    const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const imgUrl = `${backendBase}/api/share/card/${code}.png`;

    useEffect(() => {
        let cancelled = false;
        axios.get(`${backendBase}/api/share/card/${code}.json`)
            .then(r => { if (!cancelled) setData(r.data); })
            .catch(e => { if (!cancelled && e?.response?.status === 410) setGone(true); });
        return () => { cancelled = true; };
    }, [code, backendBase]);

    return (
        <div className="min-h-screen bg-background text-foreground">
            <SiteNav />
            <div className="mx-auto max-w-4xl px-5 sm:px-8 py-14">
                <div className="inline-flex items-center gap-2 rounded-full bg-secondary border border-border px-3 py-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    <Eye className="h-3.5 w-3.5" /> Public · no account required
                </div>

                {gone ? (
                    <Card className="mt-8 p-12 text-center border-dashed border-border" data-testid="won-gone">
                        <h1 className="font-serif text-4xl leading-tight">This link was revoked.</h1>
                        <p className="mt-3 text-muted-foreground max-w-md mx-auto">The person who shared it took it down. You can still run your own free scan.</p>
                        <Button asChild size="lg" className="mt-6 rounded-full bg-foreground text-background hover:bg-foreground/90">
                            <Link to="/onboarding">Start my free scan <ArrowRight className="h-4 w-4 ml-1" /></Link>
                        </Button>
                    </Card>
                ) : (
                    <>
                        <h1 className="mt-6 font-serif text-5xl sm:text-6xl leading-tight tracking-tight">
                            {data?.headline || "Someone you know found money they were owed."}
                        </h1>

                        <Card className="mt-8 p-4 border-border shadow-elegant overflow-hidden" data-testid="won-card-frame">
                            <img src={imgUrl} alt="Share card" className="w-full rounded-lg block" data-testid="won-card-img" />
                        </Card>

                        <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
                            Owed scans federal + state grants, unclaimed property, missed tax credits, and active settlements,
                            then drafts the paperwork. <span className="font-medium text-foreground">$5 one-time. Lifetime access.</span>
                        </p>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button asChild size="lg" className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-12 px-7" data-testid="won-cta-primary">
                                <Link to="/onboarding">Run my free scan <ArrowRight className="h-4 w-4 ml-1" /></Link>
                            </Button>
                            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7" data-testid="won-cta-demo">
                                <Link to="/demo">See a sample report</Link>
                            </Button>
                        </div>

                        <div className="mt-12 inline-flex items-center gap-3 rounded-full border border-border bg-card px-4 py-2 shadow-soft">
                            <ShieldCheck className="h-4 w-4 text-success" />
                            <span className="text-sm">The sender's name and the programs they applied to are <strong>never</strong> shown on this page.</span>
                        </div>
                    </>
                )}
            </div>
            <SiteFooter />
        </div>
    );
}
